import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { withNamespaces } from 'react-i18next';
import DateRangePicker from 'react-bootstrap-daterangepicker-maxspan';
import { DateFormats } from '../../../utils/Dates';
import 'bootstrap-daterangepicker/daterangepicker.css';
import Input from './Input';
import { GLOBAL } from '../../../utils/Constants';
import LabelR from '../../Simple/LabelR';

const DateFormik = (props) => {
  const {
    startFieldName,
    form: { values, setFieldValue, errors, touched },
    field: { name = startFieldName, handleBlur, handleChange },
    t,
    endFieldName,
    customRanges,
    haveClear,
    dateFormat,
    className,
    timePicker,
    timePickerIncrement,
    timePickerSeconds,
    maxDate,
    minDate,
    maxSpan,
    withRanges,
    opensTo,
    dropsTo,
    placeholder,
    singleDatePicker,
    timePicker24Hour,
    alwaysShowCalendars,
    resultFormat,
    disabled,
    required,
    requiredFields,
    label,
    disableMaxDate,
    touchedEnabled = true,
    CustomError
  } = props;

  const initFieldName = singleDatePicker ? name : startFieldName;
  const startDate = values[initFieldName] ? moment(values[initFieldName], resultFormat) : undefined;
  const endDate = values[endFieldName] ? moment(values[endFieldName], resultFormat) : undefined;
  const isTouched = (name) => (touchedEnabled ? touched[name] : true);
  const classname = classNames(
    className,
    'form-control',
    { 'is-invalid': (errors[initFieldName] && isTouched(initFieldName)) || (errors[endFieldName] && isTouched(endFieldName)) }
  );
  const requiredFieldsRender = requiredFields && requiredFields.length > 0 ? requiredFields : [name];

  const config = {
    ranges: {
      [t('dateTimeRangeLastHour')]: [moment().subtract(1, 'hours'), moment()],
      [t('dateTimeRangeLastHour', { count: 12 })]: [moment().subtract(12, 'hours'), moment()],
      [t('dateTimeRangeToday')]: [moment().startOf('day'), moment()],
      [t('dateTimeRangeYesterday')]: [moment().startOf('day').subtract(1, 'days'), moment().subtract(1, 'days').endOf('day')],
      [t('dateTimeRangeLastDay', { count: 7 })]: [moment().subtract(7, 'days'), moment()],
      [t('dateTimeRangeThisMonth')]: [moment().startOf('month'), moment().endOf('month')],
      [t('dateTimeRangeLastMonth')]: [moment().subtract(1, 'months').startOf('month'), moment().subtract(1, 'months').endOf('month')],
      ...customRanges,
    },
    locale: {
      format: dateFormat,
      separator: ' - ',
      applyLabel: t('dateTimeRangeApply'),
      cancelLabel: haveClear ? t('dateTimeRangeClear') : t('dateTimeRangeCancel'),
      weekLabel: 'W',
      customRangeLabel: t('dateTimeRangeCustomRange'),
      daysOfWeek: moment.weekdaysMin(),
      monthNames: moment.monthsShort(),
      firstDay: moment.localeData(moment.locale() === 'pt' ? 'pt-BR' : '').firstDayOfWeek(),
    },
  };

  const handleEvent = (event, picker) => {
    if (event.type === 'show' || event.type === 'hide') return;
    let { startDate, endDate } = picker;
    if (haveClear && event.type === 'cancel') {
      startDate = undefined;
      endDate = undefined;
    }
    handleRangeChanged(startDate, endDate);
  };

  const handleRangeChanged = (from, to) => {
    setFieldValue(initFieldName, from ? from.format(resultFormat) : undefined);
    setFieldValue(endFieldName, to ? to.format(resultFormat) : undefined);
  };

  const { ranges, locale } = config;
  const showRanges = withRanges && !singleDatePicker ? ranges : undefined;
  const start = startDate ? startDate.format(dateFormat) : '';
  const end = endDate ? endDate.format(dateFormat) : '';

  let textValue = start;
  if (start !== end && !singleDatePicker) {
    textValue = `${textValue} - ${end}`;
  }

  return (
    <DateRangePicker
      timePicker={timePicker}
      timePicker24Hour={timePicker24Hour}
      timePickerIncrement={timePickerIncrement}
      timePickerSeconds={timePickerSeconds}
      alwaysShowCalendars={alwaysShowCalendars}
      maxDate={!disableMaxDate ? maxDate : undefined}
      minDate={minDate}
      dateLimit={maxSpan}
      startDate={startDate}
      endDate={singleDatePicker ? startDate : endDate}
      ranges={showRanges}
      locale={locale}
      opens={opensTo}
      drops={dropsTo}
      onEvent={handleEvent}
      singleDatePicker={singleDatePicker}
      containerStyles={{ display: 'block' }}
    >
      <div className="form-group" id={`${name}-container-date-input`}>
        {label &&
          (required ? (
            <LabelR requiredFields={requiredFieldsRender} isEditable={!disabled} htmlFor={name}>
              {label}
            </LabelR>
          ) : (
            <label htmlFor={name}>{label}</label>
          ))}
        <Input
          withIcon
          type="text"
          name={name}
          value={textValue}
          handleBlur={handleBlur}
          handleChange={handleChange}
          placeholder={placeholder}
          className={classname}
          iconClasses={{ 'color-dark': true, 'icon-with-pointer': true }}
          icon="calendar"
          autoComplete="off"
          disabled={disabled}
          lastPassIgnore
        />
        {CustomError && errors[name] && isTouched(name) && <CustomError errorMessage={errors[name]} showIcon={false} />}
      </div>
    </DateRangePicker>
  );
};

DateFormik.defaultProps = {
  haveClear: false,
  timePicker: true,
  timePickerIncrement: 1,
  timePickerSeconds: true,
  maxSpan: { days: 59, hours: 23, minutes: 59, seconds: 59 },
  opensTo: GLOBAL.LEFT,
  dropsTo: GLOBAL.DOWN,
  placeholder: '',
  name: 'rangeDateTime',
  maxDate: moment().endOf('day'),
  handleRange: () => {},
  customRanges: {},
  withRanges: true,
  dateFormat: DateFormats.RegularDateTime,
  startFieldName: 'from',
  endFieldName: 'to',
  singleDatePicker: false,
  timePicker24Hour: true,
  alwaysShowCalendars: true,
  disabled: false,
  required: false,
  requiredFields: [],
  label: '',
  resultFormat: 'X',
};

DateFormik.propsTypes = {
  haveClear: PropTypes.bool,
  timePicker: PropTypes.bool,
  timePickerIncrement: PropTypes.number,
  timePickerSeconds: PropTypes.bool,
  maxSpan: PropTypes.object,
  opensTo: PropTypes.string,
  dropsTo: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  maxDate: PropTypes.object,
  minDate: PropTypes.object,
  handleRange: PropTypes.func,
  customRanges: PropTypes.shape({}),
  className: PropTypes.string,
  withRanges: PropTypes.bool,
  dateFormat: PropTypes.string,
  startFieldName: PropTypes.string,
  endFieldName: PropTypes.string,
  singleDatePicker: PropTypes.bool,
  timePicker24Hour: PropTypes.bool,
  alwaysShowCalendars: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  requiredFields: PropTypes.array,
  label: PropTypes.string,
  resultFormat: PropTypes.string,
  disableMaxDate: PropTypes.bool,
};

export default withNamespaces()(DateFormik);
